import type { RideSummary, RideTrackPoint } from '@motogo/shared';
import { useEffect, useState } from 'react';

import { fetchRide, fetchRideTrack } from '@/lib/rides';
import { getLocalRideDetail } from '@/lib/rideStore';

export type RideDetailState =
  | { status: 'loading' }
  | { status: 'ready'; summary: RideSummary; track: RideTrackPoint[] }
  | { status: 'notfound' }
  | { status: 'error'; error: string };

/** Loads a single ride's summary + full track for the detail screen. */
export function useRideDetail(id: string): RideDetailState {
  const [state, setState] = useState<RideDetailState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const [summary, track] = await Promise.all([fetchRide(id), fetchRideTrack(id)]);
        if (cancelled) return;
        if (summary && track) {
          setState({ status: 'ready', summary, track });
          return;
        }
        // Not on the server (yet) — fall back to the local copy so a just-recorded ride is
        // reviewable before it uploads, and any ride is reviewable offline.
        const local = await getLocalRideDetail(id);
        if (cancelled) return;
        setState(
          local && local.track.length > 0
            ? { status: 'ready', summary: local.summary, track: local.track }
            : { status: 'notfound' },
        );
      } catch (e) {
        if (cancelled) return;
        // Server errored (offline?) — try local before giving up.
        const local = await getLocalRideDetail(id);
        if (cancelled) return;
        if (local && local.track.length > 0) {
          setState({ status: 'ready', summary: local.summary, track: local.track });
        } else {
          setState({ status: 'error', error: e instanceof Error ? e.message : 'Failed to load ride' });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  return state;
}
